import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { consultarUbicaciones } from '../Services/ubicacionConfiguracionInicialService';
import { cambiarUbicacion, resolverUbicacion } from '../Utils/ubicacionConfiguracionInicial';

const useCatalogo = (nivel, padre) => {
  const cache = useRef(new Map());
  const [reintento, setReintento] = useState(0);
  const [estado, setEstado] = useState({ clave: null, opciones: [], error: false });
  const clave = `${nivel}:${padre}`;
  const habilitado = nivel === 'PROVINCIA' || !!padre;
  useEffect(() => {
    let vigente = true;
    if (!habilitado) return () => { vigente = false; };
    const existente = cache.current.get(clave);
    if (existente) setEstado({ clave, opciones: existente, error: false });
    else {
      setEstado({ clave: null, opciones: [], error: false });
      consultarUbicaciones(nivel, padre).then(opciones => {
        if (!vigente) return;
        cache.current.set(clave, opciones);
        setEstado({ clave, opciones, error: false });
      }).catch(() => { if (vigente) setEstado({ clave, opciones: [], error: true }); });
    }
    return () => { vigente = false; };
  }, [clave, habilitado, nivel, padre, reintento]);
  const actual = habilitado && estado.clave === clave;
  return { opciones: actual ? estado.opciones : [], cargando: habilitado && !actual,
    error: actual && estado.error, habilitado, reintentar: () => setReintento(x => x + 1) };
};

export const UbicacionConfiguracionInicial = ({ valor, onChange, disabled, onValidacion }) => {
  const fields = valor?.fields || {};
  const provincias = useCatalogo('PROVINCIA', 'DO');
  const provincia = resolverUbicacion(provincias.opciones, fields['EMP-05.PROVINCIA']);
  const municipios = useCatalogo('MUNICIPIO', provincia);
  const municipio = resolverUbicacion(municipios.opciones, fields['EMP-05.MUNICIPIO']);
  const barrios = useCatalogo('BARRIO', municipio);
  const barrio = resolverUbicacion(barrios.opciones, fields['EMP-05.BARRIO']);
  const subbarrios = useCatalogo('SUBBARRIO', barrio);
  const niveles = [['PROVINCIA', 'Provincia', provincias], ['MUNICIPIO', 'Municipio', municipios],
    ['BARRIO', 'Barrio', barrios], ['SUBBARRIO', 'Subbarrio', subbarrios]];
  let mensaje = '';
  for (const [campo, etiqueta, catalogo] of niveles) {
    const original = fields[`EMP-05.${campo}`];
    if (catalogo.cargando) { mensaje = 'Espera a que se carguen las ubicaciones.'; break; }
    if (catalogo.error) { mensaje = `Reintenta la carga de ${etiqueta.toLowerCase()} antes de continuar.`; break; }
    if (original && !resolverUbicacion(catalogo.opciones, original)) { mensaje = `Selecciona ${etiqueta.toLowerCase()} desde el catálogo correspondiente.`; break; }
  }
  if (String(fields['EMP-05.CALLE'] || '').length > 255) mensaje = 'La dirección no puede superar 255 caracteres.';
  useEffect(() => { onValidacion?.(mensaje); }, [mensaje, onValidacion]);
  return <div className="ci-form-grid">
    <Form.Group className="ci-field" controlId="EMP-05-PAIS">
      <Form.Label>País</Form.Label>
      <Form.Select value="DO" disabled><option value="DO">República Dominicana</option></Form.Select>
    </Form.Group>
    {niveles.map(([campo, etiqueta, catalogo]) => {
      const original = fields[`EMP-05.${campo}`] ?? '';
      const seleccionado = resolverUbicacion(catalogo.opciones, original);
      const pendiente = original !== '' && !seleccionado;
      return <Form.Group className="ci-field" controlId={`EMP-05-${campo}`} key={campo}>
        <Form.Label>{etiqueta}</Form.Label>
        <Form.Select value={pendiente ? '__pendiente__' : seleccionado}
          disabled={disabled || !catalogo.habilitado || catalogo.cargando || catalogo.error}
          aria-busy={catalogo.cargando}
          onChange={event => onChange(cambiarUbicacion(valor, campo, event.target.value))}>
          <option value="">{catalogo.cargando ? 'Cargando opciones…' : !catalogo.habilitado
            ? `Selecciona primero ${campo === 'MUNICIPIO' ? 'una provincia' : campo === 'BARRIO' ? 'un municipio' : 'un barrio'}`
            : `Selecciona ${etiqueta.toLowerCase()}`}</option>
          {pendiente && <option value="__pendiente__" disabled>{original} — pendiente de validar</option>}
          {catalogo.opciones.map(item => <option key={item.Id} value={item.Id}>{item.Nombre}</option>)}
        </Form.Select>
        {catalogo.error && <Alert variant="danger">No se pudo cargar {etiqueta.toLowerCase()}. <Button variant="link" onClick={catalogo.reintentar} disabled={disabled}>Reintentar</Button></Alert>}
        {catalogo.habilitado && !catalogo.cargando && !catalogo.error && !catalogo.opciones.length && <Form.Text>No hay opciones registradas para esta selección.</Form.Text>}
        {pendiente && catalogo.habilitado && !catalogo.cargando && !catalogo.error && <Form.Text>Selecciona el registro correspondiente del catálogo.</Form.Text>}
      </Form.Group>;
    })}
    <Form.Group className="ci-field ci-field--wide" controlId="EMP-05-DIRECCION">
      <Form.Label>Dirección</Form.Label>
      <Form.Control value={fields['EMP-05.CALLE'] || ''} disabled={disabled} maxLength={255}
        placeholder="Calle, número, edificio o apartamento, referencia…"
        onChange={event => onChange(cambiarUbicacion(valor, 'CALLE', event.target.value))} />
      {String(fields['EMP-05.CALLE'] || '').length > 255 && <Form.Text>La dirección supera 255 caracteres. Revisa y abrevia el texto antes de continuar.</Form.Text>}
    </Form.Group>
  </div>;
};
